import { useEffect, useState } from "react";
import { api, formatErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { toast } from "sonner";
import { Zap } from "lucide-react";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function AdminSalary() {
  const now = new Date();
  const [employees, setEmployees] = useState([]);
  const [requests, setRequests] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState("");
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [summary, setSummary] = useState(null);
  const [bonus, setBonus] = useState(0);
  const [deduction, setDeduction] = useState(0);
  const [openTransfer, setOpenTransfer] = useState(false);

  const loadRequests = () => api.get("/salary/requests").then((r) => setRequests(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/employees").then((r) => setEmployees(r.data));
    loadRequests();
  }, []);

  const calculate = async (empId = selectedEmp, m = month, y = year) => {
    if (!empId) { toast.error("Select an employee"); return; }
    try {
      const { data } = await api.get(`/salary/calculate/${empId}?month=${m}&year=${y}`);
      setSummary(data);
    } catch (err) { toast.error(formatErr(err)); }
  };

  const processRequest = async (req) => {
    setSelectedEmp(req.employee_id);
    setMonth(req.month);
    setYear(req.year);
    try {
      const { data } = await api.get(`/salary/calculate/${req.employee_id}?month=${req.month}&year=${req.year}`);
      setSummary(data);
      setBonus(0); setDeduction(0);
      setOpenTransfer(true);
    } catch (err) { toast.error(formatErr(err)); }
  };

  const transfer = async () => {
    try {
      await api.post("/salary/transfer", {
        employee_id: selectedEmp,
        month: Number(month), year: Number(year),
        bonus: Number(bonus) || 0, deduction: Number(deduction) || 0,
      });
      toast.success("Transfer initiated · processing for 24 hours");
      setOpenTransfer(false);
      setSummary(null);
      loadRequests();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const totalFinal = summary ? (summary.base_salary + summary.reimbursement_total + Number(bonus || 0) - Number(deduction || 0)) : 0;

  const pendingReqs = requests.filter((r) => r.status === "pending");
  const otherReqs = requests.filter((r) => r.status !== "pending");

  const reqBadge = (s) => ({
    pending: "bg-amber-50 text-amber-700 border-amber-300",
    processing: "bg-[#4F46E5]/10 text-[#4F46E5] border-[#4F46E5]/30",
    paid: "bg-emerald-50 text-emerald-700 border-emerald-300",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-300",
  }[s] || "bg-zinc-100 dark:bg-zinc-800");

  return (
    <div className="space-y-6" data-testid="admin-salary-page">
      <div>
        <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-600 mb-2">04 / Payroll</div>
        <h1 className="rule-text text-3xl sm:text-4xl text-zinc-900 dark:text-zinc-100">Salary Manager.</h1>
      </div>

      {/* Salary Requests Section */}
      <div className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-xl shadow-sm">
        <div className="px-6 py-4 border-b border-zinc-200 dark:border-zinc-800 flex items-baseline justify-between">
          <div>
            <div className="eyebrow">Employee Salary Requests</div>
            <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">Requests raised by employees for monthly salary</div>
          </div>
          <div className="eyebrow text-amber-700">Pending: {pendingReqs.length}</div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Employee</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Period</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Requested</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Status</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {requests.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-10 text-zinc-500 dark:text-zinc-400 text-sm">No salary requests yet.</TableCell></TableRow>
            ) : [...pendingReqs, ...otherReqs].map((r) => (
              <TableRow key={r.id} data-testid={`salary-req-${r.id}`}>
                <TableCell className="font-bold">{r.employee_name}<div className="text-xs text-zinc-500 dark:text-zinc-400 font-normal">{r.employee_id}</div></TableCell>
                <TableCell className="font-mono">{String(r.month).padStart(2,"0")}/{r.year}</TableCell>
                <TableCell>{r.created_at?.slice(0,10)}</TableCell>
                <TableCell><span className={`eyebrow px-2 py-1 border ${reqBadge(r.status)}`}>{r.status}</span></TableCell>
                <TableCell className="text-right">
                  {r.status === "pending" && (
                    <Button size="sm" className="klein-btn rounded-xl h-8 uppercase tracking-[0.18em] text-xs font-bold" onClick={() => processRequest(r)} data-testid={`process-${r.id}`}>
                      <Zap size={12} className="mr-1" /> Process
                    </Button>
                  )}
                  {r.status === "processing" && <span className="text-xs text-zinc-500 dark:text-zinc-400 italic">In 24h window</span>}
                  {r.status === "paid" && <span className="text-xs text-emerald-600 eyebrow">Completed</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Manual calculator */}
      <div>
        <div className="eyebrow mb-3">Manual Calculator</div>
        <div className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-xl shadow-sm p-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 items-end">
          <div>
            <Label className="eyebrow">Employee</Label>
            <Select value={selectedEmp} onValueChange={setSelectedEmp}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="salary-employee-select"><SelectValue placeholder="Choose..." /></SelectTrigger>
              <SelectContent>
                {employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.full_name} ({e.employee_id}) · ₹{(e.monthly_salary ?? 5000).toLocaleString("en-IN")}/mo</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="eyebrow">Month</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="salary-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="eyebrow">Year</Label>
            <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="rounded-xl mt-1" data-testid="salary-year-input" />
          </div>
          <Button className="klein-btn rounded-xl uppercase tracking-[0.2em] text-xs font-bold h-10" onClick={() => calculate()} data-testid="calculate-salary-btn">Calculate</Button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-testid="salary-summary">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-xl shadow-sm p-6">
            <div className="eyebrow mb-4">Attendance · {summary.employee_name}</div>
            <div className="space-y-3 text-sm">
              <Row label="Type" value={summary.employee_type} />
              <Row label="Full Days" value={summary.full_days} />
              <Row label="Partial Hours" value={summary.partial_hours} />
              <Row label="Monthly Base" value={`₹ ${Number(summary.monthly_salary).toLocaleString("en-IN")}`} />
              <Row label="Per Day / Hour" value={`₹${summary.per_day} / ₹${summary.per_hour}`} />
            </div>
          </div>
          <div className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-xl shadow-sm p-6">
            <div className="eyebrow mb-4">Salary Breakdown</div>
            <div className="space-y-3 text-sm">
              <Row label="Base Salary" value={`₹ ${summary.base_salary.toFixed(2)}`} />
              <Row label="Reimbursements" value={`₹ ${summary.reimbursement_total.toFixed(2)}`} />
              <Row label="Approved Bills" value={summary.reimbursements.length} />
            </div>
          </div>
          <div className="bg-[#4F46E5] text-white p-6 flex flex-col justify-between">
            <div>
              <div className="eyebrow text-white/60">Net Payable</div>
              <div className="rule-text text-4xl mt-3">₹ {(summary.base_salary + summary.reimbursement_total).toFixed(2)}</div>
              <div className="text-xs text-white/70 mt-2">Before bonus/deduction adjustments</div>
            </div>
            <Button className="rounded-xl bg-white text-[#4F46E5] hover:bg-zinc-100 dark:bg-zinc-800 uppercase tracking-[0.2em] text-xs font-bold h-11 mt-6" onClick={() => setOpenTransfer(true)} data-testid="open-transfer-btn">
              Transfer Salary →
            </Button>
          </div>
        </div>
      )}

      <Dialog open={openTransfer} onOpenChange={setOpenTransfer}>
        <DialogContent className="rounded-xl">
          <DialogHeader><DialogTitle className="rule-text text-2xl">Confirm Transfer</DialogTitle></DialogHeader>
          {summary && (
            <div className="space-y-4">
              <div className="bg-zinc-50 p-4 space-y-2 text-sm border border-zinc-200 dark:border-zinc-800">
                <Row label="Employee" value={`${summary.employee_name} (${summary.employee_type})`} />
                <Row label="Period" value={`${String(month).padStart(2,"0")}/${year}`} />
                <Row label="Base Salary" value={`₹ ${summary.base_salary.toFixed(2)}`} />
                <Row label="Reimbursements" value={`₹ ${summary.reimbursement_total.toFixed(2)}`} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="eyebrow">Bonus</Label>
                  <Input type="number" value={bonus} onChange={(e) => setBonus(e.target.value)} className="rounded-xl mt-1" data-testid="bonus-input" />
                </div>
                <div>
                  <Label className="eyebrow">Deduction</Label>
                  <Input type="number" value={deduction} onChange={(e) => setDeduction(e.target.value)} className="rounded-xl mt-1" data-testid="deduction-input" />
                </div>
              </div>
              <div className="bg-zinc-950 text-white p-4 flex justify-between items-baseline">
                <span className="eyebrow text-white/60">Final Total</span>
                <span className="rule-text text-3xl">₹ {totalFinal.toFixed(2)}</span>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" className="rounded-xl" onClick={() => setOpenTransfer(false)}>Cancel</Button>
                <Button className="klein-btn rounded-xl uppercase tracking-[0.2em] text-xs font-bold" onClick={transfer} data-testid="confirm-transfer-btn">Confirm Transfer</Button>
              </div>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Status will be "Processing" for 24 hours before being marked Completed.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between border-b border-zinc-100 dark:border-zinc-800 pb-2 last:border-0">
    <span className="text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">{label}</span>
    <span className="font-bold">{value}</span>
  </div>
);
